import React, { useState, useEffect, useRef } from 'react';
import { useParams, useOutletContext, Link, useNavigate } from 'react-router-dom';
import { formatPrice } from "../utils/formatPrice";
import axios from 'axios';
import '../styles/ViewProduct.css';

const ViewProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [specifications, setSpecifications] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const notificationTimeout = useRef(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const {
    isLoggedIn = false,
    refreshCart = () => {},
    handleCategoryClick = (category) => {
      navigate(`/home/category/${category.id}`);
    }
  } = useOutletContext() || {};

  useEffect(() => {    window.scrollTo(0, 0);
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await axios.get( `${API_BASE_URL}/ewe/products/${id}`);
        const productData = response.data;

        setProduct(productData);

        const paths = [
          { name: 'Home', path: '/home' }
        ];

        if (productData.acMainCategory) {
          paths.push({
            name: productData.acMainCategory,
            category: {
              id: productData.anMainCategoryKey,
              name: productData.acMainCategory,
              type: 'main'
            }
          });
        }

        if (productData.acCategory) {
          paths.push({
            name: productData.acCategory,
            category: {
              id: productData.anCategoryKey,
              name: productData.acCategory,
              type: 'category'
            }
          });
        }

        if (productData.acSubCategory) {
          paths.push({
            name: productData.acSubCategory,
            category: {
              id: productData.anSubCategoryKey,
              name: productData.acSubCategory,
              type: 'subcategory'
            }
          });
        }

        paths.push({
          name: (productData.acDept ? productData.acDept + " " : "") + (productData.acName || productData.name),
          isCurrent: true
        });

        setBreadcrumbs(paths);

        try {
          const specsResponse = await axios.get(
             `${API_BASE_URL}/products/${productData.anProductKey || id}/specifications`
          );
          setSpecifications(specsResponse.data);
        } catch (specError) {
          console.error('Error fetching specifications:', specError);
        }

      } catch (err) {
        console.error('Error fetching product:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const getProcessedImages = () => {
    // If no images in product data or invalid array, return fallback
    if (!product?.urlImages?.length || !Array.isArray(product.urlImages)) {
      return [{
        url: '/noImage.jpg',
        thumbnailUrl: '/noImage.jpg',
        type: 'image'
      }];
    }

    // Process and filter images
    const processed = product.urlImages
      .filter(img => {
        // Filter out 3D images and images without valid URL
        const isValidImage = img.acType?.toLowerCase() !== '3d' &&
                            img.acImage &&
                            img.acImage.startsWith('http');
        return isValidImage;
      })
      .map(img => ({
        url: img.acImage,
        thumbnailUrl: img.acThumbnail || img.acImage,
        type: img.acType?.toLowerCase() || 'image',
        isVideo: img.acType?.toLowerCase() === 'video'
      }));

    // If no valid images after filtering, return fallback
    if (processed.length === 0) {
      return [{
        url: '/noImage.jpg',
        thumbnailUrl: '/noImage.jpg',
        type: 'image'
      }];
    }

    return processed;
  };

  const addToCart = async () => {
    if (!product) return;

    try {
      // Get user data from localStorage (cookies will be sent automatically)
      const storedUser = localStorage.getItem('user');
      const currentUser = storedUser ? JSON.parse(storedUser) : null;

      // With cookies, we determine login status based on user data
      const userIsLoggedIn = !!(currentUser?.id);

      // Get or create guest cart ID if not logged in
      let guestCartId = localStorage.getItem('guestCartId');
      if (!userIsLoggedIn && !guestCartId) {
        guestCartId = 'guest-' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('guestCartId', guestCartId);
      }

      console.log('ViewProduct Auth check:', {
        hasUser: !!currentUser,
        userId: currentUser?.id,
        guestCartId: guestCartId,
        userIsLoggedIn,
        cookieAuth: 'Will be sent automatically'
      });

      const productId = product.id || product.anProductKey;
      if (!productId) {
        throw new Error('Invalid product ID');
      }

      // Build the payload
      const payload = {
        productId: productId,
        productKey: product.anProductKey,
        productCode: product.acProduct,
        productName: product.acName,
        quantity: 1,
        price: product.anPrice || product.price,
        imageUrl: product.urlImages?.[0]?.acImage || null,
        retailPrice: product.anRetailPrice || product.anPrice
      };

      // Determine which cart to use - same logic as EweProductCard
      if (userIsLoggedIn) {
        payload.userId = currentUser.id;
        console.log('Adding to user cart:', currentUser.id);
      } else {
        payload.guestCartId = guestCartId;
        console.log('Adding to guest cart:', guestCartId);
      }

      console.log('ViewProduct Cart payload:', payload);

      // Make the API call - cookies will be sent automatically
      const response = await fetch(`${API_BASE_URL}/cart/add`, {
        method: 'POST',
        credentials: 'include', // This ensures cookies are sent
        headers: {
          'Content-Type': 'application/json'
          // No Authorization header needed - cookies handle authentication
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('ViewProduct Cart add error:', errorData);

        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          // Authentication failed - clear user data
          localStorage.removeItem('user');
          console.log('Authentication failed, clearing user data');
          alert('Your session has expired. Please log in again or continue as guest.');
          return;
        }

        throw new Error(errorData.message || 'Failed to add to cart');
      }

      const result = await response.json();
      console.log('ViewProduct Cart add success:', result);

      // Refresh cart
      if (refreshCart) {
        await refreshCart();
      }

      // Clear any existing timeout
      if (notificationTimeout.current) {
        clearTimeout(notificationTimeout.current);
      }

      // Show notification and set timeout to hide it
      setShowNotification(true);
      notificationTimeout.current = setTimeout(() => {
        setShowNotification(false);
      }, 3000);

    } catch (error) {
      console.error('Error adding to cart:', error);
      alert(`Greška pri dodavanju u korpu: ${error.message}`);
    }
  };

  const renderSpecifications = () => {
    if (!specifications?.length) {
      return <div className="no-">Trenutno nema dostupnih specifikacija za ovaj proizvod</div>;
    }

    const groupedSpecs = specifications.reduce((groups, spec) => {
      const groupName = spec.groupName || 'General';
      groups[groupName] = groups[groupName] || [];
      if (spec.name !== groupName) groups[groupName].push(spec);
      return groups;
    }, {});

    return (
      <div className="specification-groups">
        {Object.entries(groupedSpecs).map(([groupName, specs]) => (
          specs.length > 0 && (
            <div className="spec-group" key={groupName}>
              {groupName !== 'General' && <h4 className="group-title">{groupName}</h4>}
              <div className="group-content">
                {specs.map((spec, index) => (
                  <div className="spec-item" key={`${groupName}-${index}`}>
                    <div className="spec-main">
                      <span className="spec-name">{spec.name}:</span>
                      <span className="spec-value"> {spec.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        ))}
      </div>
    );
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error || !product) return <div className="error">Proizvod nije pronađen</div>;

  const images = getProcessedImages();
  const currentImage = images[selectedImageIndex] || images[0];
  const documents = product.acDocument
    ? Array.isArray(product.acDocument)
      ? product.acDocument
      : [product.acDocument]
    : [];

  const price = product.anPrice || 0;
  const retailPrice = product.anRetailPrice || 0;

  const displayPrice = () => {
    if (retailPrice <= 0) {
      return price;
    }

    if (price > retailPrice && price > 0) {
      return price;
    }

    return retailPrice;
  };

  const discount = product.anDiscount || 0;
  const finalPrice = displayPrice();
  const salePrice = retailPrice * (1 - discount / 100);

  return (

    <div className="view-product-container">

      <div className="breadcrumb-container">
        <div className="breadcrumb">
          {breadcrumbs.map((item, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span className="breadcrumb-separator"> / </span>}
              {item.isCurrent ? (
                <span className="breadcrumb-current">{item.name}</span>
              ) : item.category ? (
<button
  className="breadcrumb-link"
  onClick={() => {
    handleCategoryClick?.(item.category);
    navigate(`/home/category/${item.category.id}/${item.category.name.toLowerCase().replace(/\s+/g, '-')}`, {
      state: {
        selectedCategory: item.category,
        fromProductPage: true
      }
    });
  }}
>
  {item.name}
</button>
              ) : (
                <Link to={item.path} className="breadcrumb-link">{item.name}</Link>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="product-container">
        <div className="product-images">
          <div className="main-image-container">
            {images.length > 1 && (
              <>
                <button className="carousel-btn prev-btn" onClick={() => setSelectedImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1)}>
                  &lt;
                </button>
                <button className="carousel-btn next-btn" onClick={() => setSelectedImageIndex(prev => prev === images.length - 1 ? 0 : prev + 1)}>
                  &gt;
                </button>
              </>
            )}

            <img
              src={currentImage.url}
              alt={product.acName || product.name}
              className="main-image"
              onError={(e) => {
                e.target.src = currentImage.thumbnailUrl || '/noImage.jpg';
              }}
            />
          </div>

          {images.length > 1 && (
            <div className="thumbnail-container">
              {images.map((img, index) => (
                <img
                  key={index}
                  src={img.thumbnailUrl || '/noImage.jpg'}
                  alt={`Thumbnail ${index + 1}`}
                  className={`thumbnail ${index === selectedImageIndex ? 'active' : ''}`}
                  onClick={() => setSelectedImageIndex(index)}
                  onError={(e) => { e.target.src = '/noImage.jpg' }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="product-info-view">
          <h1 className="product-title">
            {product.acDept ? `${product.acDept} ` : ""}
            {product.acName || product.name}
          </h1>

          <div className="price-section">
            {product.isPromotion === 1 || discount > 0 ? (
              <span className="sale-price">
                {formatPrice(salePrice)} RSD
              </span>
            ) : (
              <span className="price">
                {formatPrice(finalPrice)} RSD
              </span>
            )}
          </div>

          {product.filterIcons?.length > 0 && (
            <div className="filter-icons-container">
              {product.filterIcons.map((iconId, index) => (
                <img
                  key={index}
                  src={`https://resource.ewe.rs/attribute_values/${iconId}.png`}
                  alt=""
                  className="filter-icon"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ))}
            </div>
          )}
                            {showNotification && (
                              <div className="notification">
                                Proizvod dodat u korpu ✓
                              </div>
                            )}
          <button className="add-to-cart-button" onClick={addToCart}>
            Dodaj u korpu
          </button>

          {documents.length > 0 && (
            <div className="product-documents">
              <h3>Dokumentacija</h3>
              <ul>
                {documents.map((doc, index) => (
                  <li key={index}>
                    <a href={doc} target="_blank" rel="noopener noreferrer" className="document-link">
                      Preuzmi dokument {index + 1}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(product.acLongDescription || product.description) && (
            <div className="product-description">
              <h3>Opis proizvoda</h3>
              <p>{product.acLongDescription || product.description}</p>
            </div>
          )}

          <div className="product-specifications">
            <h3>Specifikacije</h3>
            {renderSpecifications()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewProduct;